import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import HomeNav from '../navigations/homenav';
import Footer from '../navigations/Footer';
import {
  PhoneIcon,
  MapPinIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useLanguageContext } from '../navigations/homenav';
import L from 'leaflet';
import { Helmet } from 'react-helmet-async';

// 🔹 Formspree endpoint – REPLACE with your real one from formspree.io
const CONTACT_FORM_ENDPOINT = 'https://formspree.io/f/xyzooadj';

// Map component: AAAPD main (blue) + 11 OPDs (red) using Leaflet + OpenStreetMap
const AAAPDMap = () => {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Prevent re-initializing if Leaflet already attached
    // @ts-ignore
    if (mapRef.current._leaflet_id) return;

    // AAAPD main office – https://maps.app.goo.gl/Xb4dhjBngskp7sSVA
    const aaapdPosition = [9.0412742, 38.7538701];
    const aaapdPhones = [
      '+251 91 355 9869',
      '+251 91 318 9881',
      '0911977333'
    ];

    const map = L.map(mapRef.current);

    // Free OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    // Custom icons (blue for AAAPD, red for OPDs)
    const blueIcon = L.divIcon({
      className: '',
      html: `<span style="
        width:24px;height:24px;border-radius:50%;
        background:#2563EB;border:3px solid #1D4ED8;display:inline-block;
      "></span>`
    });

    const redIcon = L.divIcon({
      className: '',
      html: `<span style="
        width:20px;height:20px;border-radius:50%;
        background:#DC2626;border:3px solid #B91C1C;display:inline-block;
      "></span>`
    });

    // Collect all marker coordinates for bounds
    const allCoords = [aaapdPosition];

    // AAAPD Main Office (blue)
    L.marker(aaapdPosition, { icon: blueIcon })
      .addTo(map)
      .bindPopup(
        `<div style="min-width: 200px;">
          <div style="font-weight: 600; margin-bottom: 6px;">AAAPD Main Office</div>
          <div style="margin-bottom: 8px; font-size: 13px; line-height: 1.4;">
            <div><strong>Phone 1:</strong> ${aaapdPhones[0]}</div>
            <div><strong>Phone 2:</strong> ${aaapdPhones[1]}</div>
            <div><strong>Phone 3:</strong> ${aaapdPhones[2]}</div>
          </div>
          <a href="https://www.google.com/maps?q=${aaapdPosition[0]},${aaapdPosition[1]}" target="_blank" rel="noopener noreferrer" style="color: #2563EB; text-decoration: underline; font-size: 13px;">
            Show location
          </a>
        </div>`
      );


    // 11 OPD locations (red)
    const opdLocations = [
      {
        name: 'Yeka Sub-City OPD',
        coords: [9.0229959, 38.7995959],
        phones: ['0912321443']
      },
      {
        name: 'Bole Sub-City OPD',
        coords: [9.0182537, 38.8029421],
        phones: ['0911984981']
      },
      {
        name: 'Gulele Sub-City OPD',
        coords: [9.0593078, 38.7356249],
        phones: ['0912846075']
      },
      {
        name: 'Akaki Kaliti Sub-City OPD',
        coords: [8.8972011, 38.7715356],
        phones: ['0901421768']
      },
      {
        name: 'Kolfe Keraniyo Sub-City OPD',
        coords: [9.0020498, 38.698819],
        phones: ['0911573336']
      },
      {
        name: 'Addis Ketema Sub-City OPD',
        coords: [9.0480431, 38.7213444],
        phones: ['0922960187']
      },
      {
        name: 'Lideta Sub-City OPD',
        coords: [9.0141588, 38.736148],
        phones: ['0911336893']
      },
      {
        name: 'Arada Sub-City OPD',
        coords: [9.0355685, 38.7580088],
        phones: ['0910627580']
      },
      {
        name: 'Nefas Silk Lafto Sub-City OPD',
        coords: [8.9357651, 38.7336617],
        phones: ['0912409717']
      },
      {
        name: 'Kirkos Sub-City OPD',
        coords: [9.0102667, 38.7661129],
        phones: ['0911697434']
      },
      {
        name: 'Lemi Kura Sub-City OPD',
        coords: [9.0165742, 38.872036],
        phones: ['0911669882']
      }
    ];

    opdLocations.forEach(loc => {
      const [lat, lng] = loc.coords;
      allCoords.push(loc.coords);
      L.marker(loc.coords, { icon: redIcon })
        .addTo(map)
        .bindPopup(
          `<div style="min-width: 220px;">
            <div style="font-weight: 600; margin-bottom: 6px;">${loc.name}</div>
            <div style="margin-bottom: 8px; font-size: 13px; line-height: 1.4;">
              ${(loc.phones || []).map((p, i) => `<div><strong>Phone ${i + 1}:</strong> ${p}</div>`).join('')}
            </div>
            <a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="color: #2563EB; text-decoration: underline; font-size: 13px;">
              Show location
            </a>
          </div>`
        );
    });

    // Fit map view to show all markers at once
    if (allCoords.length > 0) {
      const bounds = L.latLngBounds(allCoords);
      map.fitBounds(bounds, { padding: [40, 40] });
    } else {
      map.setView(aaapdPosition, 12);
    }

    // Cleanup on unmount
    return () => {
      map.remove();
    };
  }, []);

  return (
    <div
      ref={mapRef}
      className="w-full h-full min-h-[400px] rounded-2xl overflow-hidden"
    />
  );
};

const ContactUsPage = () => {
  const { t } = useLanguageContext();

  // 🔹 form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // "success" | "error" | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);

    try {
      const res = await fetch(CONTACT_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json', // Formspree returns JSON if asked
        },
        body: JSON.stringify(formData),
      });


      if (!res.ok) throw new Error('Request failed');

      // Formspree response is usually { ok: true, ... }
      const data = await res.json().catch(() => ({}));
      if (data.ok !== false) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white text-gray-800">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Contact AAAPD – Get in Touch</title>
        <meta
          name="description"
          content="Reach out to the Addis Ababa Association of Persons with Disabilities (AAAPD) for inquiries, support, partnerships, or membership. Find our contact info, office location, and form to connect with us."
        />
        <link rel="canonical" href="https://aaapd.org.et/contactus" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <HomeNav />

      <main id="main">
        {/* Hero Section */}
        <section
          className="relative py-24 md:py-48 bg-cover bg-center text-white"
          style={{ backgroundImage: "url('/homeimagemain2.jpg')" }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50 filter grayscale"></div>
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              {t('contactUs')}
            </h1>
            <p className="mt-4 text-lg text-gray-300">
              <Link to="/" className="hover:underline">
                {t('home')} /
              </Link>{' '}
              {t('contactUs')}
            </p>
          </div>
        </section>

        {/* Map and Info Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-16 w-16 flex items-center justify-center bg-sky-600 text-white rounded-xl">
                  <MapPinIcon className="h-8 w-8" />
                </div>
                <div className="ml-5">
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('contactTitle')}
                  </h3>
                  <p className="mt-1 text-gray-600">
                    1888 Building | Piassa Dejach Wube|, Addis Ababa, Ethiopia
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-16 w-16 flex items-center justify-center bg-orange-500 text-white rounded-xl">
                  <PhoneIcon className="h-8 w-8" />
                </div>
                <div className="ml-5">
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('phoneNumber')}
                  </h3>
                  <p className="mt-1 text-gray-600">+251 91 355 9869</p>
                  <p className="text-gray-600">+251 91 318 9881</p>
                  <p className="text-gray-600">+251 91 197 7333</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-16 w-16 flex items-center justify-center bg-sky-600 text-white rounded-xl">
                  <EnvelopeIcon className="h-8 w-8" />
                </div>
                <div className="ml-5">
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('emailAddress')}
                  </h3>
                  <p className="mt-1 text-gray-600">aapda2546@gmail.com</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-16 w-16 flex items-center justify-center bg-orange-500 text-white rounded-xl">
                  <QuestionMarkCircleIcon className="h-8 w-8" />
                </div>
                <div className="ml-5">
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('contactTitle')}
                  </h3>
                  <p className="mt-1 text-gray-600">
                    {t('contactSubtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Standalone Map Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {t('contactMapTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('contactMapSubtitle')}
              </p>
            </div>
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <div className="w-full h-96 rounded-lg shadow-lg overflow-hidden">
                <AAAPDMap />
              </div>
            </div>
            <div className="flex justify-center items-center gap-8">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-600 rounded-full border border-blue-800"></div>
                <span className="text-sm font-medium text-gray-700">
                  AAAPD Main Office
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-600 rounded-full border border-red-800"></div>
                <span className="text-sm font-medium text-gray-700">
                  OPDs in 11 Sub-Cities
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Office Hours and Social Media Section */}
        {/* keep your existing Office Hours + Social Media section here if you had it before */}

        {/* Contact Form Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
            {/* Left side: Image */}
            <div>
              <img
                src="/contactusformimage2.jpeg"
                alt="Person holding a donation box"
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            {/* Right side: Form */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder={t('yourName')}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border-gray-300 rounded-full shadow-sm py-3 px-6 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder={t('emailAddress')}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border-gray-300 rounded-full shadow-sm py-3 px-6 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="phone"
                    placeholder={t('phoneNumber')}
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border-gray-300 rounded-full shadow-sm py-3 px-6 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <textarea
                    rows="5"
                    name="message"
                    placeholder={t('typeMessage')}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border-gray-300 rounded-2xl shadow-sm py-3 px-6 focus:border-teal-500 focus:ring-teal-500"
                  ></textarea>
                </div>
                <div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full px-6 py-3 rounded-full text-lg font-semibold bg-teal-700 text-white hover:bg-teal-800 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (t('sending') || 'Sending...') : t('sendMessage')}
                  </button>
                </div>
              </form>

              {status === 'success' && (
                <p className="mt-4 text-sm text-green-600">
                  {t('contactSuccess') || 'Thank you! Your message has been sent.'}
                </p>
              )}
              {status === 'error' && (
                <p className="mt-4 text-sm text-red-600">
                  {t('contactError') || 'Something went wrong. Please try again.'}
                </p>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUsPage;
